import React from "react"
import { createContext, useContext, useState } from "react"
import { LoadingScreen } from "../interfaceParts/LoadingScreen"
import { assert, doo } from "@cd2/tools"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useLocationReload } from "../hooks/locationReloadKey"
import { useSkeem } from "../providers/SkeemProvider"
import { IRecord } from "../types/schema"
import { LoadingFrame } from "../pages/user/UserResultsShowPage/AssessmentDetailsTab"
import { useParams } from "react-router-dom"
import { ManagedModalProvider } from "../providers/ManagedModals"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { useReloadKey } from "../hooks/useReloadKey"
import { explodePromise } from "./explodePromise"
import { unique } from "./array"

type GlobalState = {
  user: IRecord<
    "skeem_users",
    | "isManager"
    | "isOwner"
    | "isAdmin"
    | "name"
    | "canCreateHRProfile"
    | "isReadonly"
    | "downloadPolicy"
    | "downloadPolicyContracts"
    | "dismissedMicrosoftBanner"
    | "isEmployee"
  > & {
    employee: IRecord<"employees"> | null
    managedTeams: IRecord<"teams">[]
  }
  companyId: string
  headCompanyId: string | null
  accessibleCompanyIds: string[]
  /** Sub company IDs the user has chosen to see in the "My Sub Companies" cases tab. */
  focusedSubCompanyIds: string[]
  /** The head company id if we're in a head/sub structure, otherwise the current company id. Always non-null. */
  baseCompanyId: string
  /** The single default team id for the base company (where teams live), if one exists. */
  defaultTeamId: string | null
  /** The team the current user is in. Falls back to defaultTeamId when the user isn't in any team. */
  myTeamId: string | null
  company: Company
  permissions: {
    canSkipFlowGuidance: boolean
    canManageEmployees: boolean
    employeeVisibility: "team" | "all"
    canManageCompany: boolean
    canAccessEmployeeFiles: boolean
    canSeeAllCases: boolean

    fileAccess: "needApproval" | "allowed"
    fileAccessContracts: "blocked" | "needApproval" | "allowed"
  }
  settings: Settings
  inAdminArea: boolean

  reload(): Promise<void>
}

const companyAttributes = [
  "name",
  "isHeadCompany",
  "isSubCompany",
  "isAdminCompany",
  "inheritSettings",
  "inheritFlows",
  "inheritFiles",
  "downloadPolicy",

  "primaryColor",
  "secondaryColor",
  "imageFileboyId",
  "letterHeadImageFileboyId",
  "logoIsPartnership",
  "letterHead",
  "inheritLetterHead",
] as const

type Company = IRecord<"companies", (typeof companyAttributes)[number]> & {
  subCompanies: IRecord<"companies">[]
  companyTags: IRecord<"companyTags", "name">[]
  userCompanyTags: IRecord<"userCompanyTags", "name">[]
}

const settingAttributes = [
  "disableEmployeeFeatures",
  "allowEmployeeLogin",
  "canHaveTeams",
  "downloadPolicy",
  "canManageEmployees",
  "canSeeHrProfessionalsArea",
  "canSeeHrTeamsArea",
  "canSkipFlowGuidance",
  "hideIntroFlowLink",
  "canUseManageCaseCoaching",
  "canEmailFiles",
  "canEmailFilesPermission",
  "caseInactivityNotificationEmail",
  "caseInactivityNotificationTime",
  "caseNamePlaceholderText",
  "casesRequireEmployees",
  "defaultBreakDuration",
  "disableNonEmailLogin",
  "employeeVisibility",
  "employeeFilesAccess",
  "flowNotesRequired",
  "freeAccount",
  "holidayResetDate",
  "hrUsersCanSeeAllCases",
  "knowledgeBaseOnly",
  "levelOnePermissionLabel",
  "levelTwoPermissionLabel",
  "managersCanSeeAllCases",
  "notifyAboutStaleAfterDays",
  "prefix",
  "repeatStaleNotificationEveryDays",
  "reportAccessPermission",
  "timeTakenReportAccessPermission",
  "subscriptionIncludesOneHourForHr",
  "useKnowledgeBase",
  "useLocations",
  "useMFA",
  "useRota",
  "useWhiteLabelling",
  "enableBillingDashboard",
  "enableFourthIntegration",
  "enableITrentIntegration",
  "enableProbations",
  "closeCategoryOptions",
  "noteTypeOptions",
  "disableRealCases",
] as const

type Settings = Omit<IRecord<"companies", (typeof settingAttributes)[number]>, "id">

const GlobalStateContext = createContext<GlobalState | null>(null)

export function GlobalStateProvider({ children }: { children: React.ReactNode }) {
  const skeem = useSkeem()
  const [reloadKey, triggerReload] = useLocationReload()
  const [state, setState] = useState<GlobalState | null>(null)
  const afterLoadCbRef = React.useRef<(() => void) | null>(null)

  ;(window as any).globalState = state

  useAsyncEffect(async () => {
    type User = IRecord<
      "skeem_users",
      | "isManager"
      | "isOwner"
      | "isAdmin"
      | "name"
      | "canCreateHRProfile"
      | "isReadonly"
      | "downloadPolicy"
      | "downloadPolicyContracts"
      | "dismissedMicrosoftBanner"
      | "isEmployee"
    > & {
      company:
        | (Company &
            Settings & {
              headCompany:
                | (IRecord<"companies"> &
                    Settings & {
                      subCompanies: IRecord<"companies">[]
                      companyTags: IRecord<"companyTags", "name">[]
                    })
                | null
            })
        | null
      accessibleCompanyIds: string[]
      focusedSubCompanyIds: string[]
      employee: IRecord<"employees"> | null
      managedTeams: IRecord<"teams">[]
    }
    const user = await skeem.fetchMe<User>([
      "isManager",
      "isOwner",
      "isAdmin",
      "name",
      "canCreateHRProfile",
      "isReadonly",
      "downloadPolicy",
      "downloadPolicyContracts",
      "dismissedMicrosoftBanner",
      "isEmployee",
      {
        name: "company",
        attributes: [
          ...companyAttributes,
          "subCompanies",
          { name: "companyTags", attributes: ["name"] },
          { name: "userCompanyTags", attributes: ["name"] },
          ...settingAttributes,
          {
            name: "headCompany",
            attributes: [
              ...(settingAttributes as any),
              "subCompanies",
              { name: "companyTags", attributes: ["name"] },
              { name: "userCompanyTags", attributes: ["name"] },
            ],
          },
        ],
      },
      { as: "accessibleCompanyIds", value: { path: ["accessibleCompanies", "id"] } },
      {
        as: "focusedSubCompanyIds",
        value: { path: ["focusedSubCompanyLinks", "company", "id"] },
      },
      "employee",
      "managedTeams",
    ])
    assert(user.company, "User has no company")

    const company = user.company
    // Teams live on the head company; when there's no head, the current company acts as its own base.
    const baseCompanyId = company.headCompany?.id ?? company.id

    const teams = await skeem.fetch2<
      (IRecord<"teams", "defaultTeam" | "downloadPolicy"> & { selfInTeam: boolean })[]
    >({
      model: {
        name: "teams",
        attributes: [
          "defaultTeam",
          "downloadPolicy",
          {
            as: "selfInTeam",
            value: { matches: { members: { some: { id: { session: true } } } } },
          },
        ],
        filter: {
          and: [
            { matches: { company: baseCompanyId } },
            {
              or: [
                { matches: { defaultTeam: true } },
                { matches: { members: { some: { id: { session: true } } } } },
              ],
            },
          ],
        },
      },
    })
    const defaultTeam = teams.find(team => team.defaultTeam) ?? null
    const myTeam = teams.find(team => team.selfInTeam) ?? defaultTeam

    const settings =
      user.company.headCompany && user.company.inheritSettings
        ? user.company.headCompany
        : user.company

    const newState = {
      user: user as GlobalState["user"],
      companyId: company.id,
      headCompanyId: company.headCompany?.id ?? null,
      baseCompanyId,
      defaultTeamId: defaultTeam?.id ?? null,
      myTeamId: myTeam?.id ?? null,
      accessibleCompanyIds:
        // If we are not in the admin company then we set the accessibleCompanyIds to only those related to the current company
        user.isAdmin && !user.company.isAdminCompany
          ? unique(
              [
                user.company.id,
                ...user.company.subCompanies.map(c => c.id),
                user.company.headCompany?.id,
                ...(user.company.headCompany?.subCompanies?.map(c => c.id) || []),
              ].filter(Boolean),
            )
          : unique([user.company.id, ...user.accessibleCompanyIds]),
      focusedSubCompanyIds: user.focusedSubCompanyIds ?? [],
      company: company,
      settings: settings,
      permissions: computeUserPermissions(
        user as GlobalState["user"],
        settings,
        myTeam ?? undefined,
        company,
      ),
      inAdminArea: user.company.isAdminCompany,
      reload: handleReload,
    }

    if (JSON.stringify(newState) !== JSON.stringify(state)) {
      setState(newState)
    }

    if (afterLoadCbRef.current) {
      afterLoadCbRef.current()
      afterLoadCbRef.current = null
    }
  }, [reloadKey])

  const handleReload = useAsyncCallback(async () => {
    const exploded = explodePromise<void>()
    afterLoadCbRef.current = exploded.resolve
    triggerReload()
    await exploded.promise
  })

  if (!state) return <LoadingScreen />

  return <GlobalStateContext.Provider value={state}>{children}</GlobalStateContext.Provider>
}

function computeUserPermissions(
  user: GlobalState["user"],
  settings: Settings,
  myTeam?: IRecord<"teams", "downloadPolicy">,
  company?: Company,
): GlobalState["permissions"] {
  // Letters: member override (if set) else team / sub-company default.
  const inheritedLetterAccess = company?.isSubCompany
    ? company.downloadPolicy || "allowed"
    : myTeam?.downloadPolicy || "allowed"
  const fileAccess = user.downloadPolicy || inheritedLetterAccess

  // Contracts: per-user policy (empty = allowed). No team default.
  const fileAccessContracts = user.downloadPolicyContracts || "allowed"

  return {
    canSeeAllCases:
      user.isAdmin ||
      user.isOwner ||
      (user.isManager && settings.managersCanSeeAllCases) ||
      (user.canCreateHRProfile && settings.hrUsersCanSeeAllCases),
    canSkipFlowGuidance: doo(() => {
      if (settings.canSkipFlowGuidance === "everyone") return true
      if (
        settings.canSkipFlowGuidance === "teamLeader" &&
        (user.managedTeams.length > 0 || user.isManager || user.isOwner || user.isAdmin)
      )
        return true
      if (
        settings.canSkipFlowGuidance === "admin" &&
        (user.isManager || user.isOwner || user.isAdmin)
      )
        return true

      return false
    }),
    canManageEmployees: doo(() => {
      if (settings.canManageEmployees === "everyone") return true
      if (user.isAdmin || user.isManager || user.isOwner) return true
      return false
    }),
    employeeVisibility: user.isManager || user.isAdmin ? "all" : settings.employeeVisibility,
    canManageCompany: user.isAdmin || user.isOwner || user.isManager,
    canAccessEmployeeFiles:
      settings.employeeFilesAccess === "anybody" || user.isAdmin || user.isOwner || user.isManager,

    fileAccess: fileAccess as GlobalState["permissions"]["fileAccess"],
    fileAccessContracts: fileAccessContracts,
  }
}

export function AdminCompaniesGlobalStateProvider({ children }: { children: React.ReactNode }) {
  const myGlobalState = useGlobalState()
  const { id } = useParams<{ id: string }>()
  const [reloadKey, triggerReload] = useReloadKey()
  const afterLoadCbRef = React.useRef<(() => void) | null>(null)

  type CompanyWithHead = Company & {
    headCompany?: { id: string; subCompanies: { id: string }[] } | null
  }

  const [data, setData] = useState<{
    company: CompanyWithHead
    headCompanyId: string | null
    settings: Settings
    defaultTeamId: string | null
  } | null>(null)

  useAsyncEffect(
    async ({ isAlive }) => {
      if (data) setData(null)
      const record = await window.skeem.fetch2<
        Company &
          Settings & {
            headCompany:
              | ((IRecord<"companies"> & Settings) & { subCompanies: { id: string }[] })
              | null
          }
      >({
        first: {
          name: "companies",
          matches: { id },
          attributes: [
            ...companyAttributes,
            "subCompanies",
            ...settingAttributes,
            {
              name: "headCompany",
              attributes: [...settingAttributes, { name: "subCompanies" }] as any,
            },
          ],
        },
      })
      if (!isAlive()) return

      const baseCompanyId = record.headCompany?.id ?? record.id
      const defaultTeam = await window.skeem.fetch2<IRecord<"teams"> | null>({
        first: {
          name: "teams",
          filter: { matches: { company: baseCompanyId, defaultTeam: true } },
        },
      })
      if (!isAlive()) return

      setData({
        company: record,
        headCompanyId: record.headCompany?.id ?? null,
        settings: record.headCompany && record.inheritSettings ? record.headCompany : record,
        defaultTeamId: defaultTeam?.id ?? null,
      })
      if (afterLoadCbRef.current) {
        afterLoadCbRef.current()
        afterLoadCbRef.current = null
      }
    },
    [id, reloadKey],
  )

  const handleReload = useAsyncCallback(async () => {
    await myGlobalState.reload()
    const exploded = explodePromise<void>()
    afterLoadCbRef.current = exploded.resolve
    triggerReload()
    await exploded.promise
  })

  if (!data) return <LoadingFrame />

  const accessibleCompanyIds = unique(
    [
      data.company.id,
      ...(data.company.subCompanies?.map((c: { id: string }) => c.id) ?? []),
      data.headCompanyId,
      ...(data.company.headCompany?.subCompanies?.map((c: { id: string }) => c.id) ?? []),
    ].filter(Boolean),
  )

  const baseCompanyId = data.headCompanyId ?? id

  return (
    <GlobalStateContext.Provider
      value={{
        company: data.company,
        companyId: id,
        headCompanyId: data.headCompanyId,
        baseCompanyId,
        defaultTeamId: data.defaultTeamId,
        // Admin area acts on behalf of other companies; treat the default team as the user's team.
        myTeamId: data.defaultTeamId,
        accessibleCompanyIds,
        focusedSubCompanyIds: [],
        user: myGlobalState.user,
        settings: data.settings,
        permissions: computeUserPermissions(myGlobalState.user, data.settings),
        inAdminArea: true,
        reload: handleReload,
      }}
    >
      <ManagedModalProvider />
      {children}
    </GlobalStateContext.Provider>
  )
}

export function useGlobalState() {
  const value = useContext(GlobalStateContext)

  assert(value, "GlobalStateProvider not found")

  return value
}

export function AnonymousGlobalStateProvider({ children }: { children: React.ReactNode }) {
  return (
    <GlobalStateContext.Provider
      value={{
        companyId: "anonymous",
        headCompanyId: null,
        accessibleCompanyIds: [],
        focusedSubCompanyIds: [],
        baseCompanyId: "anonymous",
        defaultTeamId: null,
        myTeamId: null,
        permissions: {
          canSeeAllCases: false,
          canSkipFlowGuidance: false,
          canManageEmployees: false,
          employeeVisibility: "all",
          canManageCompany: false,
          canAccessEmployeeFiles: false,
          fileAccess: "allowed",
          fileAccessContracts: "allowed",
        },
        settings: {
          allowEmployeeLogin: false,
          canEmailFiles: false,
          canEmailFilesPermission: "everyone",
          canHaveTeams: false,
          canManageEmployees: "admins",
          canSeeHrProfessionalsArea: false,
          canSeeHrTeamsArea: false,
          canSkipFlowGuidance: "admins",
          canUseManageCaseCoaching: false,
          caseInactivityNotificationEmail: null,
          caseInactivityNotificationTime: null,
          caseNamePlaceholderText: "",
          casesRequireEmployees: false,
          closeCategoryOptions: "",
          defaultBreakDuration: 0,
          disableEmployeeFeatures: false,
          disableNonEmailLogin: false,
          disableRealCases: false,
          downloadPolicy: "allowed",
          employeeFilesAccess: "anybody",
          employeeVisibility: "all",
          enableBillingDashboard: false,
          enableFourthIntegration: false,
          enableITrentIntegration: false,
          enableProbations: false,
          flowNotesRequired: false,
          freeAccount: false,
          hideIntroFlowLink: false,
          holidayResetDate: null,
          hrUsersCanSeeAllCases: false,
          knowledgeBaseOnly: false,
          levelOnePermissionLabel: "",
          levelTwoPermissionLabel: "",
          managersCanSeeAllCases: false,
          noteTypeOptions: "",
          notifyAboutStaleAfterDays: 0,
          prefix: "",
          repeatStaleNotificationEveryDays: 0,
          reportAccessPermission: "system_admin",
          subscriptionIncludesOneHourForHr: false,
          timeTakenReportAccessPermission: "system_admin",
          useKnowledgeBase: false,
          useLocations: false,
          useMFA: false,
          useRota: false,
          useWhiteLabelling: false,
        },
        inAdminArea: false,
        reload: () => Promise.resolve(),
        user: {
          id: "anonymous",
          name: "Anonymous",
          isAdmin: false,
          isManager: false,
          isOwner: false,
          canCreateHRProfile: false,
          isReadonly: false,
          downloadPolicy: "",
          downloadPolicyContracts: "allowed",
          dismissedMicrosoftBanner: null,
          isEmployee: false,
          employee: null,
          managedTeams: [],
        },
        company: {
          id: "anonymous",
          companyTags: [],
          inheritFiles: false,
          name: "Anonymous",
          isHeadCompany: false,
          isSubCompany: false,
          isAdminCompany: false,
          inheritSettings: false,
          inheritFlows: false,
          inheritLetterHead: false,
          primaryColor: "#000000",
          secondaryColor: "#000000",
          imageFileboyId: "",
          letterHeadImageFileboyId: "",
          logoIsPartnership: false,
          letterHead: null,
          downloadPolicy: "allowed",
          subCompanies: [],
          userCompanyTags: [],
        },
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  )
}
